import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Drawer,
  IconButton,
  TextField,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import SearchIcon from "@material-ui/icons/Search";
import { useEffect, useState } from "react";
import {
  EPOCUS_BG,
  EPOCUS_BORDER,
  EPOCUS_PRIMARY,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  EPOCUS_WHITE,
  rem,
} from "../epocus-palette";
import type {
  PatientSearchCriteria,
  PatientSearchResult,
} from "./use-patient-search";
import { usePatientSearch } from "./use-patient-search";

type Props = {
  open: boolean;
  onClose: () => void;
  onSelect: (patient: PatientSearchResult) => void;
};

const EMPTY_FORM: Required<PatientSearchCriteria> = {
  firstName: "",
  middleName: "",
  lastName: "",
  dob: "",
  phoneNumber: "",
  nationalIdNumber: "",
};

const maskPhone = (phone: string) => {
  const digits = phone.replace(/\D/g, "");
  if (digits.length <= 4) return digits;
  return `••• ${digits.slice(-4)}`;
};

const buildSecondaryLine = (patient: PatientSearchResult) => {
  const parts: string[] = [];
  if (patient.dob) parts.push(`DOB ${patient.dob}`);
  if (patient.nationalIdNumber) {
    parts.push(`ID ${patient.nationalIdNumber}`);
  } else if (patient.phone) {
    parts.push(`☎ ${maskPhone(patient.phone)}`);
  }
  return parts.join("  ·  ");
};

const FieldLabel: FCwC = ({ children }) => (
  <Typography
    style={{
      fontSize: rem(11),
      fontWeight: 700,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: EPOCUS_TEXT_SECONDARY,
      marginBottom: 4,
    }}
  >
    {children}
  </Typography>
);

export const PatientSearchDialog: FC<Props> = ({ open, onClose, onSelect }) => {
  const [form, setForm] = useState<Required<PatientSearchCriteria>>(EMPTY_FORM);
  const [submitted, setSubmitted] = useState(false);
  const { search, patients, loading } = usePatientSearch();

  // Reset the form and hide prior results when the dialog reopens, so a
  // previous session's candidate rows can't be mistaken for the current one.
  // The `submitted` gate hides the cached hook results until a new search runs.
  useEffect(() => {
    if (open) {
      setForm(EMPTY_FORM);
      setSubmitted(false);
    }
  }, [open]);

  const update =
    (field: keyof PatientSearchCriteria) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      setForm((prev) => ({ ...prev, [field]: value }));
    };

  const hasAnyCriteria = Object.values(form).some((v) => v.trim().length > 0);

  const handleSearch = () => {
    if (!hasAnyCriteria) return;
    setSubmitted(true);
    search(form);
  };

  const handleClearSearch = () => {
    setForm(EMPTY_FORM);
    setSubmitted(false);
  };

  const handleSelect = (patient: PatientSearchResult) => {
    onSelect(patient);
    onClose();
  };

  const inputStyle = { backgroundColor: EPOCUS_WHITE };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        style: { backgroundColor: "transparent", boxShadow: "none" },
      }}
    >
      <Box
        style={{
          borderRadius: "16px 16px 0 0",
          padding: "12px 24px 32px",
          maxHeight: "88vh",
          display: "flex",
          flexDirection: "column",
          backgroundColor: EPOCUS_WHITE,
          maxWidth: 680,
          margin: "0 auto",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <Box
          style={{
            width: 40,
            height: 4,
            backgroundColor: EPOCUS_BORDER,
            borderRadius: 2,
            margin: "0 auto 20px",
          }}
        />

        <Box
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 8,
            marginBottom: 4,
          }}
        >
          <Typography
            style={{
              fontSize: rem(20),
              fontWeight: 700,
              color: EPOCUS_TEXT_PRIMARY,
            }}
          >
            Find a patient
          </Typography>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label="Close patient search"
            style={{ color: EPOCUS_TEXT_SECONDARY, marginTop: -4 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Typography
          style={{
            fontSize: rem(13),
            color: EPOCUS_TEXT_SECONDARY,
            marginBottom: 20,
          }}
        >
          Search by name, date of birth, phone number, or ID. Combine fields to
          narrow the results.
        </Typography>

        <Box style={{ overflowY: "auto", minHeight: 0 }}>
          <Box style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Box style={{ display: "flex", gap: 12 }}>
              <Box style={{ flex: 1 }}>
                <FieldLabel>First name</FieldLabel>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={form.firstName}
                  onChange={update("firstName")}
                  InputProps={{ style: inputStyle }}
                  inputProps={{ "aria-label": "First name" }}
                />
              </Box>
              <Box style={{ flex: 1 }}>
                <FieldLabel>Last name</FieldLabel>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={form.lastName}
                  onChange={update("lastName")}
                  InputProps={{ style: inputStyle }}
                  inputProps={{ "aria-label": "Last name" }}
                />
              </Box>
            </Box>

            <Box>
              <FieldLabel>Middle Name</FieldLabel>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                value={form.middleName}
                onChange={update("middleName")}
                InputProps={{ style: inputStyle }}
                inputProps={{ "aria-label": "Middle Name" }}
              />
            </Box>

            <Box>
              <FieldLabel>Date of birth</FieldLabel>
              <TextField
                fullWidth
                type="date"
                variant="outlined"
                size="small"
                value={form.dob}
                onChange={update("dob")}
                InputProps={{ style: inputStyle }}
                inputProps={{ "aria-label": "Date of birth" }}
              />
            </Box>

            <Box>
              <FieldLabel>Phone number</FieldLabel>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                value={form.phoneNumber}
                onChange={update("phoneNumber")}
                InputProps={{ style: inputStyle }}
                inputProps={{
                  "aria-label": "Phone number",
                  inputMode: "tel",
                }}
              />
            </Box>

            <Box>
              <FieldLabel>National ID Number</FieldLabel>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                value={form.nationalIdNumber}
                onChange={update("nationalIdNumber")}
                InputProps={{ style: inputStyle }}
                inputProps={{ "aria-label": "National ID Number" }}
              />
            </Box>

            <Button
              fullWidth
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              disabled={!hasAnyCriteria || loading}
              style={{
                marginTop: 4,
                backgroundColor: EPOCUS_PRIMARY,
                color: EPOCUS_WHITE,
                textTransform: "none",
                fontWeight: 700,
                fontSize: rem(15),
                padding: "10px 0",
              }}
            >
              {loading ? "Searching…" : "Search"}
            </Button>
          </Box>

          {submitted && loading && (
            <Box
              style={{
                display: "flex",
                justifyContent: "center",
                padding: "24px 0",
              }}
            >
              <CircularProgress size={24} style={{ color: EPOCUS_PRIMARY }} />
            </Box>
          )}

          {submitted && !loading && (
            <Box style={{ marginTop: 16 }} aria-live="polite">
              <Box
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 8,
                  marginBottom: 8,
                }}
              >
                <Typography
                  style={{
                    fontSize: rem(11),
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: EPOCUS_TEXT_SECONDARY,
                  }}
                >
                  {patients.length}{" "}
                  {patients.length === 1 ? "result" : "results"}
                </Typography>
                <Button
                  size="small"
                  startIcon={<CloseIcon fontSize="small" />}
                  onClick={handleClearSearch}
                  aria-label="Clear search and start again"
                  style={{
                    color: EPOCUS_PRIMARY,
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: rem(13),
                  }}
                >
                  Clear
                </Button>
              </Box>
              {patients.length > 0 ? (
                <Box
                  style={{ display: "flex", flexDirection: "column", gap: 8 }}
                >
                  {patients.map((patient) => {
                    const secondaryLine = buildSecondaryLine(patient);
                    return (
                      <ButtonBase
                        key={patient.patientId}
                        onClick={() => handleSelect(patient)}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          padding: "12px 14px",
                          borderRadius: 10,
                          border: `1.5px solid ${EPOCUS_BORDER}`,
                          backgroundColor: EPOCUS_BG,
                          width: "100%",
                          textAlign: "left",
                        }}
                      >
                        <Typography
                          style={{
                            fontSize: rem(15),
                            fontWeight: 700,
                            color: EPOCUS_TEXT_PRIMARY,
                            lineHeight: 1.3,
                          }}
                        >
                          {patient.displayName}
                        </Typography>
                        {secondaryLine && (
                          <Typography
                            style={{
                              fontSize: rem(12),
                              color: EPOCUS_TEXT_SECONDARY,
                              marginTop: 2,
                            }}
                          >
                            {secondaryLine}
                          </Typography>
                        )}
                      </ButtonBase>
                    );
                  })}
                </Box>
              ) : (
                <Typography
                  style={{
                    fontSize: rem(13),
                    color: EPOCUS_TEXT_SECONDARY,
                    textAlign: "center",
                    padding: "16px 0",
                  }}
                >
                  No patients found. Try fewer or different details.
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};
